class ProductVariantSelect extends HTMLElement {
  abortController;

  connectedCallback() {
    this.initOptions();
    this.reconcileDefaultSelection();
  }

  /**
   * EK022 — Resolve the effective default selection mode.
   * An experiment override (window.EKSTER_DEFAULT_SELECTION) wins so the
   * selection state can be swapped client-side (e.g. Intelligems) without a
   * theme rebuild. The override may be a string ('first' | 'manual' | 'none')
   * or an object { mode, variantId, value }.
   * @returns {{ mode: string, variantId?: string, value?: string }}
   */
  getDefaultSelectionConfig() {
    const override = window.EKSTER_DEFAULT_SELECTION;
    if (typeof override === 'string') return { mode: override };
    if (override && typeof override === 'object') return override;
    return { mode: this.dataset.defaultSelectionMode || 'first' };
  }

  /**
   * EK022 — Reconcile the rendered selection with the effective mode.
   * Server-rendered state is trusted for the plain (non-override) case; the
   * override path lets an experiment force a state on top of it.
   */
  reconcileDefaultSelection() {
    const config = this.getDefaultSelectionConfig();
    const mode = config.mode || 'first';
    const hasOverride = window.EKSTER_DEFAULT_SELECTION != null;
    // An explicit ?variant= means a shopper (or a deep link) has already chosen.
    // The section re-renders with this param after a selection, so the override
    // must not run then, otherwise it would fight the shopper's choice.
    const hasExplicitVariant = new URLSearchParams(window.location.search).has('variant');

    this.selectionMode = mode;

    if (hasOverride && !hasExplicitVariant) {
      if (mode === 'none') {
        this.clearSelection();
      } else if (mode === 'manual') {
        const target = this.applyManualSelection(config);
        // EK022 — The server rendered the prices of the first available
        // variant, so when the override actually changes the selection the
        // section must be re-rendered silently (no scroll, no URL update, no
        // focus) to bring the title price, add to cart price and media in
        // sync. The re-rendered HTML has the override variant checked, so the
        // next reconcile pass is a no-op and cannot loop.
        if (target) this.onVariantChange({ target }, { silent: true });
      }
    }

    // Arm the add-to-cart validation whenever nothing is selected and the mode
    // forbids a default. Covers both server-rendered and override-driven none.
    this.requiresSelection = mode === 'none' && !this.hasCheckedOption();
    this.updateAddToCartButtonsState(this.requiresSelection);
  }

  /**
   * EK022 — The add-to-cart buttons (desktop footer and mobile sticky) that
   * share this form. Located by form id since they live outside this element.
   * @returns {HTMLElement[]}
   */
  getAddToCartButtons() {
    const formId = this.input?.getAttribute('form');
    if (!formId) return [];
    return Array.from(document.querySelectorAll(`button[name="add"][form="${formId}"]`));
  }

  /**
   * EK022 — Toggle the buy button between its normal state and the "select an
   * option" prompt state, keeping desktop and mobile in sync.
   * @param {boolean} requiresSelection
   */
  updateAddToCartButtonsState(requiresSelection) {
    this.getAddToCartButtons().forEach((button) => {
      button.toggleAttribute('data-requires-selection', requiresSelection);
    });
  }

  /**
   * @returns {boolean} Whether any option radio is currently checked.
   */
  hasCheckedOption() {
    return Array.from(this.options ?? []).some((option) => option.checked);
  }

  /**
   * EK022 — Clear every option and empty the hidden variant id input.
   */
  clearSelection() {
    (this.options ?? []).forEach((option) => {
      option.checked = false;
    });
    if (this.input) this.input.value = '';
  }

  /**
   * EK022 — Pre-select a specific option without triggering the fetch flow
   * (so the page does not scroll on load).
   * @param {{ variantId?: string, value?: string }} config
   */
  applyManualSelection({ variantId, value }) {
    const all = Array.from(this.options ?? []);
    let target = null;
    if (variantId) target = all.find((option) => option.dataset.variantId == variantId);
    if (!target && value) target = all.find((option) => option.value === value);
    if (!target) return null;
    // Already the server-rendered state: nothing to change, nothing to re-render.
    if (target.checked && this.input?.value === target.dataset.variantId) return null;

    all.forEach((option) => {
      option.checked = option === target;
    });
    if (this.input) this.input.value = target.dataset.variantId;
    return target;
  }

  /**
   * EK022 — Draw attention to the selector when add-to-cart is attempted
   * without a selection. The message itself is shown via the toast.
   */
  flagRequired() {
    this.classList.add('ek-selection-required');
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * EK022 — Clear the required state once a selection is made.
   */
  clearRequired() {
    this.requiresSelection = false;
    this.classList.remove('ek-selection-required');
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.options = this.querySelectorAll('[data-variant-id]');
    this.handle = this.dataset.handle;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.qsProductId;
    this.input = this.querySelector('input[name="id"]');

    // EK-040 — No `!this.abortController` guard here anymore. It made every
    // selection during an in-flight fetch a dead click: the browser checked the
    // radio, but the handler never ran, so the price, media and hidden variant
    // id stayed on the previous choice — and when the first fetch resolved, its
    // replaceWith() snapped the radio back. That is the "jump" when comparing
    // options quickly. The in-flight request is now aborted in onVariantChange
    // instead, so the last selection always wins.
    this.addEventListener('change', (event) => {
      const target = event.target.closest('[data-variant-id]');
      if (target) this.onVariantChange({ target });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-variant-id]');
      if (target) {
        event.preventDefault();
        target.checked = true;
        this.onVariantChange({ target });
      }
    });

    this.mainProduct = this.closest('main-product');
    const stockScope = this.mainProduct ?? this;

    const originalSelector = stockScope.querySelector('[data-stock-indicator][aria-hidden="false"]');
    this.originalStockState = originalSelector?.dataset?.stockIndicator;

    this.productOptionLabels = this.querySelectorAll('[data-product-option-label]');
    this.productOptionLabels.forEach((label) => {
      const originalTitle = label?.closest('fieldset')?.querySelector('legend')?.textContent;
      // EK-046 — Limited edition swatches carry their own display name, and a
      // cross product drop is a link with no radio to read a value from.
      const hoverTitle =
        label?.dataset?.hoverTitle || label?.closest('[data-product-option]')?.querySelector('input')?.value;
      if (!hoverTitle) return;

      label.addEventListener('mouseenter', () => {
        this.handleOptionHover(label, hoverTitle);
      });

      label.addEventListener('mouseleave', () => {
        this.handleOptionHover(label, originalTitle, this.originalStockState);
      });

      label.addEventListener('click', () => {
        const originalState = label?.dataset.state;
        this.originalStockState = originalState;
        this.handleOptionHover(label, hoverTitle, originalState);
      });
    });
  }

  /**
   * Handle variant change.
   * @param {Event} event - The event object.
   */
  async onVariantChange({ target }, { silent = false } = {}) {
    const { variantId } = target.dataset;

    // EK022 — A selection has been made, so drop the no-selection validation
    // and switch the buy button out of its prompt state.
    this.clearRequired();
    this.updateAddToCartButtonsState(false);

    const params = this.createParams(variantId);

    this.input.value = variantId;
    this.input.dispatchEvent(new Event('change'));

    // Only update URL when explicitly configured (determined by Liquid logic).
    // EK022 — Never in silent mode (override reconciliation on page load).
    const shouldUpdateURL = this.dataset.updateUrl === 'true' && !silent;

    if (shouldUpdateURL) {
      this.updateURL(variantId);
    }

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    // EK-040 — Scroll on the selection itself instead of after the fetch
    // resolves. Waiting for the network was the visible delay on every
    // selection; the destination (the top of the product section) does not
    // depend on the fetched HTML, so there is nothing to wait for.
    // Gated on data-update-url so only the main product on the product page
    // scrolls, which is what the original comment below always intended —
    // quick-add and embedded product sections no longer move the page.
    if (!silent && this.dataset.updateUrl === 'true') {
      this.scrollToSectionTop();
    }

    // EK-040 — Last selection wins: abort the in-flight request instead of
    // ignoring the new selection.
    if (this.abortController) this.abortController.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    try {
      const url = `/products/${this.handle}?${params}`;
      const response = await fetch(url, {
        signal: abortController.signal,
      });

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      const newVariantSelect = parsedHTML.querySelector('product-variant-select').cloneNode(true);
      // Replace the url with the original url in case the product is in a dynamic section, otherwise the url will be updated to the new variant url that conflicts
      newVariantSelect.dataset.updateUrl = this.dataset.updateUrl;
      this.replaceWith(newVariantSelect);

      this.updateContextMenuStock(parsedHTML);

      publish(`variant:changed:${this.productId}:${this.sectionId}`, {
        variantId,
        html: parsedHTML,
      });

      // EK-040 — The scroll now happens up front in this method (see above),
      // so the fetch completing no longer moves the viewport a second time.
    } catch (error) {
      // EK-040 — A superseded request is normal during quick back-to-back
      // selections, not an error state.
      if (error.name === 'AbortError') return;

      console.error(error.message);
      window.toast.error(window.toast_translations.product.variant_error);
    } finally {
      // EK-040 — Only clear the controller this call created. A superseded
      // call finishing late must not null out the controller of the request
      // that replaced it.
      if (this.abortController === abortController) this.abortController = null;
      // EK022 — Skip focus in silent mode so the page does not scroll to the selector on load.
      // EK-040 — preventScroll is the partial-scroll fix: this focus() fired
      // right after the smooth scroll started and yanked the viewport back to
      // the radio, which read as the scroll "stopping partway".
      if (!silent) document.querySelector(`#${target.id}`)?.focus({ preventScroll: true });
    }
  }

  /**
   * EK-040 — Smooth-scroll to the top of this product's section.
   * Runs at selection time rather than after the section re-render.
   * Behavior is configurable so the team can decide per product type whether
   * scroll-on-select should exist at all:
   *   window.EKSTER_VARIANT_SCROLL ('scroll' | 'none', client override for testing)
   *   > data-scroll-on-select (product metafield custom.variant_scroll_on_select > Form block setting)
   *   > 'scroll' (current behavior).
   */
  scrollToSectionTop() {
    if (this.getScrollOnSelectMode() === 'none') return;

    const section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    if (!section) return;

    // getBoundingClientRect + scrollY is the document-absolute position and is
    // correct regardless of offsetParent, where offsetTop was not.
    const top = Math.max(0, Math.round(section.getBoundingClientRect().top + window.scrollY));
    if (Math.abs(window.scrollY - top) <= 2) return;

    window.scrollTo({ top, behavior: 'smooth' });
  }

  /**
   * EK-040 — Resolve the effective scroll-on-select mode.
   * @returns {string} 'scroll' | 'none'
   */
  getScrollOnSelectMode() {
    const override = window.EKSTER_VARIANT_SCROLL;
    if (override === 'scroll' || override === 'none') return override;
    return this.dataset.scrollOnSelect === 'none' ? 'none' : 'scroll';
  }

  /**
   * Update URL
   * @param {String} value - URL value
   */
  updateURL(value) {
    const { searchParams } = new URL(window.location);
    searchParams.set('variant', value);

    // Replace the current history state with the new search params, without reloading the page
    history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    return [`section_id=${this.sectionId}`, `variant=${variantId}`].join('&');
  }

  /**
   * @method Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.checked = option === target;
    });
  }

  /**
   * @returns {HTMLElement[]} Stock indicators within main-product scope and context menu.
   */
  getStockIndicators() {
    const scope = this.mainProduct ?? this;
    const inScope = Array.from(scope.querySelectorAll('[data-stock-indicator]'));
    const contextMenu = Array.from(
      document.querySelectorAll(`[data-context-menu-stock="${this.productId}"] [data-stock-indicator]`)
    );
    return [...inScope, ...contextMenu];
  }

  /**
   * Update the stock indicators in the context menu from parsed section HTML.
   * @param {Document} html - The parsed HTML document from the variant fetch.
   */
  updateContextMenuStock(html) {
    const contextMenuWrapper = document.querySelector(`[data-context-menu-stock="${this.productId}"]`);
    if (!contextMenuWrapper) return;

    const newWrapper = html.querySelector(`[data-context-menu-stock="${this.productId}"]`);
    if (!newWrapper) return;

    contextMenuWrapper.innerHTML = newWrapper.innerHTML;
  }

  handleOptionHover(option, newTitle, originalState) {
    const titleTarget = option.closest('fieldset').querySelector('legend');
    if (!titleTarget) return;
    titleTarget.innerHTML = newTitle;

    this.getStockIndicators().forEach((indicator) => {
      indicator.setAttribute('aria-hidden', 'true');
      if (indicator.dataset.stockIndicator === (originalState || option.dataset.state)) {
        indicator.setAttribute('aria-hidden', 'false');
      }
    });
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
